import axios from 'axios'
import { toast } from 'react-toastify'
import config from '../config/config'
import { logger } from '../utils'
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito'

const MAX_RETRIES = 3
const RETRY_STATUS_CODES = [408, 500, 502, 503, 504]
const RETRY_ERROR_CODES = ['ECONNABORTED', 'ETIMEDOUT', 'ENOTFOUND', 'ERR_INTERNET_DISCONNECTED', 'ERR_CONNECTION_RESET', 'INTERNET_DISCONNECTED']

const getRetryDelayMs = (retryCount: number): number => Math.min(1000 * (2 ** retryCount), 10000)

let timeoutCallback: () => void = () => {}

/**
 * Sets a function that will be called on timeout of after `MAX_RETRIES`.
 * Safe to be left unset (e.g. for tests), the default is a no-op function.
 */
export const setApiTimeoutCallback = (callback: () => void) => {
  timeoutCallback = callback
}

/**
 * Flag to indicate the application is in the process of logging out.
 * When true, the request interceptor will abort new API requests and
 * the error interceptor will suppress toast notifications and Sentry errors.
 */
let isLoggingOut = false

/**
 * Sets the logout state flag. Call with `true` before clearing auth tokens
 * during logout, and `false` after logout cleanup is complete.
 */
export const setLoggingOut = (value: boolean): void => {
  isLoggingOut = value
}

/**
 * Returns the current logout state. Useful for testing.
 */
export const getIsLoggingOut = (): boolean => isLoggingOut

const axiosInstance = axios.create({
  baseURL: config.baseUrl,
  timeout: 8000,
  headers: {
    Accept: 'application/json',
    Account: config.getAccountSlug()
  }
})

// Add retry logic interceptor
axiosInstance.interceptors.response.use(
  response => response,
  async error => {
    // Skip retry and timeoutCallback during logout — errors are expected
    if (isLoggingOut) {
      return await Promise.reject(error)
    }

    const config = error.config

    // Skip retry if explicitly disabled (for testing)
    if (config?.headers?.['x-retry'] === 'false') {
      return await Promise.reject(error)
    }

    // Initialize retry count
    if (config === undefined || config === null || config.retry === undefined) {
      if (config !== undefined && config !== null) {
        config.retry = MAX_RETRIES
        config.__retryCount = 0
      } else {
        timeoutCallback()
        return await Promise.reject(error)
      }
    }

    // Check if we should retry
    if (config.__retryCount !== undefined && config.__retryCount !== null && config.__retryCount >= config.retry) {
      if (config.suppressToast !== true) {
        timeoutCallback()
      }
      return await Promise.reject(error)
    }

    const status = error.response ? error.response.status : null
    const code = error.code

    const isNetworkError = error.response === undefined || error.response === null
    const isRetryableCode = code !== undefined && code !== null && RETRY_ERROR_CODES.includes(code)
    const isRetryableStatus = status !== null && RETRY_STATUS_CODES.includes(status)

    if (isNetworkError || isRetryableCode || isRetryableStatus) {
      if (config.__retryCount !== undefined) {
        config.__retryCount = (config.__retryCount as number) + 1
      } else {
        config.__retryCount = 1
      }

      const reason = isNetworkError
        ? 'no response (network error)'
        : isRetryableCode
          ? `error code: ${String(code)}`
          : `HTTP status: ${String(status)}`

      const logMessage = `Retrying ${String(config.url)} (attempt ${String(config.__retryCount)}/${String(config.retry)}) due to ${reason}`
      logger.log(logMessage)

      const delayMs = getRetryDelayMs(config.__retryCount)
      return await new Promise(resolve =>
        setTimeout(() => resolve(axiosInstance(config)), delayMs)
      )
    }

    timeoutCallback()
    return await Promise.reject(error)
  }
)

// Add authentication interceptor
axiosInstance.interceptors.request.use(
  async (config) => {
    // Reject all API requests during logout to prevent unauthenticated calls
    if (isLoggingOut) {
      return await Promise.reject(new Error('Logout in progress'))
    }

    // Skip authentication for SSO validation endpoint as it receives token in body
    if (config.url?.includes('/auth/sso/validate')) {
      logger.log(`⚡ Skipping auth header for SSO validation endpoint: ${String(config.url)}`)
      return config
    }

    // First check for SSO token in localStorage
    const ssoToken = localStorage.getItem('dopageAccessToken')
    
    if (ssoToken) {
      logger.log(`🔑 Using SSO token for API call: ${String(config.url)}`)
      config.headers.Authorization = `bearer ${ssoToken}`
      return config
    }

    // Fall back to Cognito tokens for non-SSO users
    logger.log(`🔑 Falling back to Cognito token for API call: ${String(config.url)}`)
    const tokens = await cognitoUserPoolsTokenProvider.getTokens()
    const accessToken = tokens?.accessToken?.toString()

    if (accessToken !== null && accessToken !== undefined) {
      const token = accessToken.toString()
      config.headers.Authorization = `bearer ${token}`
      logger.log('✅ Cognito token found and set')
    } else {
      logger.log(`❌ No authentication token available for API call: ${String(config.url)}`)
    }
    return config
  },
  async (error) => {
    return await Promise.reject(error)
  }
)

// Add error handling interceptor (runs after retry logic)
axiosInstance.interceptors.response.use(
  (res) => {
    return res
  },
  async (err) => {
    // Silently swallow errors during logout — they are expected and not actionable
    if (isLoggingOut) {
      return await Promise.reject(err)
    }

    // If we've already retried MAX_RETRIES times and still have an error, handle it
    const suppressToast = err.config?.suppressToast === true
    if (err.response !== null && err.response !== undefined) {
      // Access Token was expired
      if (err.response.status === 401) {
        window.location.href = '/'
      } else if (!suppressToast) {
        toast.error(err.message, { position: toast.POSITION.TOP_CENTER })
      }
      // Return the response so that the UI can manage any custom error handling.
      return err.response
    } else if (!suppressToast) {
      toast.error(err.message, { position: toast.POSITION.TOP_CENTER })
    }
    logger.error(err)
  }
)

export default axiosInstance
